import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export interface Notification {
    id: number;
    user_id: string;
    type: string;
    title: string;
    message: string;
    data: any;
    is_read: number;
    created_at: number;
}

export function useNotifications() {
    const { isAuthenticated } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchUnreadCount = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const { count } = await api.notifications.getUnreadCount();
            setUnreadCount(count);
        } catch (err) {
            console.error("Failed to fetch unread count", err);
        }
    }, [isAuthenticated]);

    const fetchNotifications = useCallback(async () => {
        if (!isAuthenticated) return;
        setLoading(true);
        try {
            const list = await api.notifications.list();
            setNotifications(list);
            // Update count after fetching list (optional, but good for sync)
            fetchUnreadCount();
        } catch (err) {
            console.error("Failed to fetch notifications", err);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, fetchUnreadCount]);

    const markAsRead = async (id: number) => {
        try {
            // Optimistic update
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));

            await api.notifications.markAsRead(id);
        } catch (err) {
            console.error("Failed to mark as read", err);
            // Revert if needed, but for 'read' status it's rarely critical
            fetchNotifications();
        }
    };

    const markAllAsRead = async () => {
        try {
            setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
            setUnreadCount(0);
            await api.notifications.markAllAsRead();
        } catch (err) {
            console.error("Failed to mark all as read", err);
            fetchNotifications();
        }
    };

    // Polling for unread count
    useEffect(() => {
        if (!isAuthenticated) return;

        const poll = () => {
            if (document.hidden) return;
            fetchUnreadCount();
        };

        fetchUnreadCount();
        const interval = setInterval(poll, 30000); // 30 seconds

        return () => clearInterval(interval);
    }, [isAuthenticated, fetchUnreadCount]);

    return {
        unreadCount,
        notifications,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        refreshCount: fetchUnreadCount
    };
}
